import { memo } from 'react';
import { CourseRequirementType, ICourseRequirement } from '@/types/courses';
import { BrandliveEvent } from '@/types/projects.types';
import Container from '@ui/container/container';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import Text from '@ui/text/text/text';
import styles from '../courses.module.css';
import TooltipPortal from '@ui/tooltip-portal/tooltip-portal';
import Button from '@ui/buttons/button/button';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import getThemeColorsCssMixVariable from '@/utils/colors/get-theme-color-css-mix-variable';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';

type CourseRequirementRowProps = {
  requirement: ICourseRequirement;
  workingEvent: BrandliveEvent | null;
  onEdit: (requirement: ICourseRequirement) => void;
  onDelete: (uuid: string) => void;
};

const CourseRequirementRow = memo(
  function CourseRequirementRow({ requirement, workingEvent, onEdit, onDelete }: CourseRequirementRowProps) {
    const name = requirement.type === CourseRequirementType.watched_minute ? 'Watchtime' : 'Pulse check';

    return (
      <Container
        background="var(--gray-900)"
        height="64px"
        key={requirement.uuid}
        className={`${styles.requirementsItem} radius-8`}
      >
        <div className="position-absolute width-100-percent height-100-percent container">
          <Container
            className="flex jc-space-between ai-center padding-l-16 padding-r-16 height-100-percent radius-12"
            background={'var(--gray-800)'}
          >
            <Text className="S1 flex gap-10 ai-center" color="var(--gray-100)">
              {name}
              <TooltipPortal
                tooltipContent={
                  requirement.type === CourseRequirementType.watched_minute ? (
                    <Text tag="span" color="var(--gray-true-dark-100)">
                      Watch {requirement.required_amount ?? 'x'} minutes to complete this requirement
                    </Text>
                  ) : (
                    <Text tag="span" color="var(--gray-true-dark-100)">
                      Demonstrate your engagement via {requirement.required_amount ?? 'x'} check-ins
                    </Text>
                  )
                }
              >
                <Icon name={ICONS.INFO_CIRCLE} color="var(--gray-400)" size={12} />
              </TooltipPortal>
            </Text>
            <Text
              backgroundColor="var(--gray-600)"
              className="S1 flex gap-10 ai-center padding-6 padding-l-12 padding-r-12 radius-8"
              color="var(--gray-100)"
            >
              {name}
            </Text>
          </Container>
        </div>
        <Container
          className={`${styles.hoverState} opacity-0 flex jc-space-between padding-l-16 height-100-percent radius-8`}
          background={'var(--gray-800)'}
        >
          <Container className="flex-column jc-center">
            <Text className="S1 flex gap-10 ai-center" color="var(--gray-100)">
              {name}
              <TooltipPortal
                tooltipContent={
                  requirement.type === CourseRequirementType.watched_minute ? (
                    <span>Watch {requirement.required_amount ?? 'x'} minutes to complete this requirement</span>
                  ) : (
                    <span>Demonstrate your engagement via {requirement.required_amount ?? 'x'} check-ins</span>
                  )
                }
              >
                <Icon name={ICONS.INFO_CIRCLE} color="var(--gray-400)" size={12} />
              </TooltipPortal>
            </Text>
            <div className="flex gap-10">
              <Text className="S2" color="var(--gray-300)">
                {workingEvent?.sessions.find((session) => session.session === requirement.session_id)?.title.base || ''}{' '}
                • Pass threshold
              </Text>
              <Text className="S2">
                {requirement.required_amount}
                {requirement.type === CourseRequirementType.watched_minute ? 'm' : ''}
              </Text>
            </div>
          </Container>
          <Container className="flex ai-center gap-10 padding-r-16">
            <Button
              className="flex jc-center ai-center radius-full padding-0"
              width="32px"
              height="32px"
              backgroundColor="var(--gray-600)"
              hoverBackgroundColor="var(--gray-500)"
              onClick={() => onDelete(requirement.uuid)}
            >
              <Icon name={ICONS.TRASH} color="var(--gray-100)" size={12} />
            </Button>
            <Button
              backgroundColor={getThemeColorCSSVariable()}
              hoverBackgroundColor={getThemeColorsCssMixVariable()}
              designSize="small"
              onClick={() => onEdit(requirement)}
            >
              <Text className="L1" color={getThemeColorContrastCSSVariable()}>
                Edit
              </Text>
            </Button>
          </Container>
        </Container>
      </Container>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.requirement.uuid === nextProps.requirement.uuid &&
      prevProps.requirement.required_amount === nextProps.requirement.required_amount &&
      prevProps.requirement.type === nextProps.requirement.type &&
      prevProps.requirement.session_id === nextProps.requirement.session_id &&
      prevProps.onEdit === nextProps.onEdit &&
      prevProps.onDelete === nextProps.onDelete
    );
  }
);

export default CourseRequirementRow;
